import { ReactNode, useState } from 'react';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Collapse } from '@mui/material';

import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
} from '../../../../../providers/portfolio-performance';
import { useThemeMode } from '../../../../../providers/theme-provider';
import { DILETTA_TITLE } from '../../../../../components/v2/widgets/exotics-shared';
import { CardTabs } from '../../../../../components/card-tabs';

import { IndexedComparisonTab } from './indexed-comparison-tab';
import { CorrelationScatterTab } from './correlation-scatter-tab';

import './styles.scss';

interface PerformanceComparisonCardProps {
  // The shared series selection (rows ticked in the Overview table above) is the
  // input to the panel AS A WHOLE, so every tab reads the same selection. Real
  // strategies and Buy-and-Hold references are treated identically; empty =>
  // nothing ticked yet.
  accounts: IPortfolioPerformanceAccounts[];
  reportsByAccount: Record<string, IPortfolioPerformanceAccountReport>;
  isNamesBlurred?: boolean;
}

interface ComparisonTab {
  key: string;
  label: string;
  content: ReactNode;
}

export const PerformanceComparisonCard = ({
  accounts,
  reportsByAccount,
  isNamesBlurred = false,
}: PerformanceComparisonCardProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeKey, setActiveKey] = useState('indexed');

  // Tab registry: additional comparisons drop in here as new entries, each
  // reading the same shared `accounts` selection. The Correlation tab pairs the
  // matrix and its scatter in one view (they share the window selector).
  const comparisonTabs: ComparisonTab[] = [
    {
      key: 'indexed',
      label: 'Indexed',
      content: (
        <IndexedComparisonTab
          accounts={accounts}
          reportsByAccount={reportsByAccount}
          isNamesBlurred={isNamesBlurred}
        />
      ),
    },
    {
      key: 'correlation',
      label: 'Correlation',
      content: (
        <CorrelationScatterTab
          accounts={accounts}
          reportsByAccount={reportsByAccount}
          isNamesBlurred={isNamesBlurred}
        />
      ),
    },
  ];

  const activeIndex = Math.max(
    0,
    comparisonTabs.findIndex((tab) => tab.key === activeKey),
  );
  const current = comparisonTabs[activeIndex] ?? comparisonTabs[0];

  return (
    <div className="performance-comparison-container">
      <div className="header">
        <h1>Index Comparison</h1>
        <button
          type="button"
          className="collapse-chevron"
          aria-label={isExpanded ? 'Collapse comparison panel' : 'Expand comparison panel'}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((prev) => !prev)}
          style={{ color: isDark ? DILETTA_TITLE : '#092D3A' }}
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </button>
      </div>

      <Collapse in={isExpanded}>
        <CardTabs
          value={activeIndex}
          onChange={(index) => setActiveKey(comparisonTabs[index].key)}
          tabs={comparisonTabs.map(({ label }) => ({ label }))}
        />
        <div className="comparison-body">{current.content}</div>
      </Collapse>
    </div>
  );
};
